import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../utils/translations';
import { Button } from '../components/UI';
import SharedHeader from '../components/SharedHeader';
import SharedFooter from '../components/SharedFooter';

const ForClinics = () => {
  const { language, changeLanguage } = useLanguage();
  const t = (key) => getTranslation(language, key);

  useEffect(() => {
    // SEO Meta Tags
    const titles = {
      es: 'LIBERADO para Clínicas Veterinarias — Plataforma IA de Contratación',
      en: 'Liberado for Veterinary Clinics — AI Shift Workforce Platform',
      pt: 'LIBERADO para Clínicas Veterinárias — Plataforma IA de Contratação'
    };

    const descriptions = {
      es: 'Plataforma impulsada por IA que ayuda a clínicas veterinarias en Chile a acceder a técnicos verificados, reducir brechas de personal y mejorar la estabilidad operativa.',
      en: 'AI-powered veterinary workforce platform helping clinics in Chile access verified technicians, reduce staffing gaps, and improve operational stability.',
      pt: 'Plataforma de força de trabalho veterinária impulsionada por IA, ajudando clínicas no Chile a acessar técnicos verificados, reduzir lacunas de pessoal e melhorar a estabilidade operacional.'
    };

    document.title = titles[language] || titles.es;

    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = descriptions[language] || descriptions.es;

    // Open Graph tags
    const ogTags = [
      { property: 'og:title', content: titles[language] || titles.es },
      { property: 'og:description', content: descriptions[language] || descriptions.es },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://liberado.app/for-clinics' },
      { property: 'og:image', content: 'https://liberado.app/og-image.png' }
    ];

    ogTags.forEach(tag => {
      let ogTag = document.querySelector(`meta[property="${tag.property}"]`);
      if (!ogTag) {
        ogTag = document.createElement('meta');
        ogTag.setAttribute('property', tag.property);
        document.head.appendChild(ogTag);
      }
      ogTag.content = tag.content;
    });
  }, [language]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white">
      <SharedHeader />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Link to="/" className="inline-flex items-center text-yellow-400 hover:text-yellow-300 mb-8 transition">
            ← {language === 'es' ? 'Volver al inicio' : language === 'en' ? 'Back to home' : 'Voltar ao início'}
          </Link>

          {/* Title */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {language === 'es' ? 'Para Clínicas Veterinarias' : language === 'en' ? 'For Veterinary Clinics' : 'Para Clínicas Veterinárias'}
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              {language === 'es' 
                ? 'Reduzca riesgos de contratación, mejore la estabilidad operativa y acceda a talento verificado en toda la red veterinaria de Chile'
                : language === 'en'
                ? 'Reduce hiring risk, improve operational stability, and access verified talent across Chile’s veterinary network'
                : 'Reduza riscos de contratação, melhore a estabilidade operacional e acesse talentos verificados em toda a rede veterinária do Chile'
              }
            </p>
          </div>

          {/* Features */}
          <div className="space-y-8">
            {/* Feature 1 */}
            <div className="bg-purple-800 bg-opacity-50 rounded-lg p-8 backdrop-blur">
              <div className="flex items-start space-x-4">
                <div className="text-4xl">✅</div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">
                    {language === 'es' ? 'Verificación de Credenciales Impulsada por IA (En Desarrollo)' : language === 'en' ? 'AI-Powered Credential Verification (In Development)' : 'Verificação de Credenciais Impulsionada por IA (Em Desenvolvimento)'}
                  </h3>
                  <p className="text-gray-200 leading-relaxed">
                    {language === 'es'
                      ? 'Nuestro futuro sistema de inteligencia artificial validará automáticamente las credenciales profesionales de cada técnico, reduciendo riesgos de contratación y protegiendo la reputación de su clínica. Esto permitirá decisiones más rápidas y seguras en un mercado donde la confianza es esencial.'
                      : language === 'en'
                      ? 'Our future artificial intelligence system will automatically validate the professional credentials of each technician, reducing hiring risks and protecting your clinic’s reputation. This will enable faster, safer decisions in a market where trust is essential.'
                      : 'Nosso futuro sistema de inteligência artificial validará automaticamente as credenciais profissionais de cada técnico, reduzindo riscos de contratação e protegendo a reputação de sua clínica. Isso permitirá decisões mais rápidas e seguras em um mercado onde a confiança é essencial.'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="bg-purple-800 bg-opacity-50 rounded-lg p-8 backdrop-blur">
              <div className="flex items-start space-x-4">
                <div className="text-4xl">📅</div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">
                    {language === 'es' ? 'Reducción de Brechas de Personal' : language === 'en' ? 'Staffing Gap Reduction' : 'Redução de Lacunas de Pessoal'}
                  </h3>
                  <p className="text-gray-200 leading-relaxed">
                    {language === 'es'
                      ? 'Acceda a una red creciente de técnicos veterinarios certificados disponibles bajo demanda. Minimice cancelaciones de último minuto, mantenga niveles de atención consistentes y mejore la eficiencia operativa sin comprometer la calidad del servicio. Una solución escalable para clínicas de cualquier tamaño.'
                      : language === 'en'
                      ? 'Access a growing network of certified veterinary technicians available on demand. Minimize last-minute cancellations, maintain consistent care levels, and improve operational efficiency without compromising service quality. A scalable solution for clinics of any size.'
                      : 'Acesse uma rede crescente de técnicos veterinários certificados disponíveis sob demanda. Minimize cancelamentos de última hora, mantenha níveis consistentes de atendimento e melhore a eficiência operacional sem comprometer a qualidade do serviço. Uma solução escalável para clínicas de qualquer tamanho.'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="bg-purple-800 bg-opacity-50 rounded-lg p-8 backdrop-blur">
              <div className="flex items-start space-x-4">
                <div className="text-4xl">🎯</div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">
                    {language === 'es' ? 'Emparejamiento Inteligente (En Desarrollo)' : language === 'en' ? 'Intelligent Matching (In Development)' : 'Correspondência Inteligente (Em Desenvolvimento)'}
                  </h3>
                  <p className="text-gray-200 leading-relaxed">
                    {language === 'es'
                      ? 'Nuestros algoritmos futuros conectarán su clínica con los técnicos más adecuados según especialidades, experiencia, ubicación y compatibilidad cultural. Esto optimizará resultados de contratación, reducirá rotación y fortalecerá la cohesión del equipo, creando valor sostenible a largo plazo.'
                      : language === 'en'
                      ? 'Our future algorithms will connect your clinic with the most suitable technicians based on specialties, experience, location, and cultural fit. This will optimize hiring outcomes, reduce turnover, and strengthen team cohesion, creating sustainable long-term value.'
                      : 'Nossos futuros algoritmos conectarão sua clínica com os técnicos mais adequados com base em especialidades, experiência, localização e adequação cultural. Isso otimizará os resultados de contratação, reduzirá a rotatividade e fortalecerá a coesão da equipe, criando valor sustentável a longo prazo.'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="bg-purple-800 bg-opacity-50 rounded-lg p-8 backdrop-blur">
              <div className="flex items-start space-x-4">
                <div className="text-4xl">🇨🇱</div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">
                    {language === 'es' ? 'Impacto Escalable para LATAM' : language === 'en' ? 'Scalable Impact for LATAM' : 'Impacto Escalável para LATAM'}
                  </h3>
                  <p className="text-gray-200 leading-relaxed">
                    {language === 'es'
                      ? 'Comenzando en Chile, LIBERADO está construyendo infraestructura para expandirse a toda Latinoamérica. Nuestro modelo aborda brechas estructurales en el mercado veterinario regional, posicionando a las clínicas participantes como líderes de innovación en un sector clave para la salud pública y el bienestar animal.'
                      : language === 'en'
                      ? 'Starting in Chile, LIBERADO is building infrastructure to expand across Latin America. Our model addresses structural gaps in the regional veterinary market, positioning participating clinics as innovation leaders in a sector critical to public health and animal welfare.'
                      : 'Começando no Chile, a LIBERADO está construindo infraestrutura para expandir por toda a América Latina. Nosso modelo aborda lacunas estruturais no mercado veterinário regional, posicionando clínicas participantes como líderes de inovação em um setor crítico para a saúde pública e o bem-estar animal.'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* AI Disclaimer */}
          <div className="mt-12 bg-yellow-500 bg-opacity-10 border border-yellow-500 rounded-lg p-6">
            <p className="text-sm text-yellow-200">
              {language === 'es'
                ? '⚠️ Algunas funciones de inteligencia artificial aún están en desarrollo y se implementarán progresivamente durante el piloto en Chile.'
                : language === 'en'
                ? '⚠️ Some artificial intelligence features are still in development and will be implemented progressively during the Chile pilot.'
                : '⚠️ Algumas funcionalidades de inteligência artificial ainda estão em desenvolvimento e serão implementadas progressivamente durante o piloto no Chile.'
              }
            </p>
          </div>

          {/* CTA Section */}
          <div className="mt-16 text-center bg-yellow-500 bg-opacity-10 border-2 border-yellow-500 rounded-lg p-8">
            <h2 className="text-3xl font-bold mb-4">
              {language === 'es' ? '¿Listo para transformar la gestión de su clínica?' : language === 'en' ? 'Ready to transform your clinic management?' : 'Pronto para transformar a gestão de sua clínica?'}
            </h2>
            <p className="text-lg text-gray-200 mb-6">
              {language === 'es'
                ? 'Únase al piloto de Chile y sea parte de la evolución de la industria veterinaria'
                : language === 'en'
                ? 'Join the Chile pilot and be part of the veterinary industry evolution'
                : 'Junte-se ao piloto do Chile e faça parte da evolução da indústria veterinária'
              }
            </p>
            <Link to="/interes">
              <Button className="bg-yellow-500 hover:bg-yellow-600 text-black text-lg px-8 py-4 font-bold shadow-xl">
                {t('registerForInvitation')} →
              </Button>
            </Link>
          </div>
        </div>
      </main>

      <SharedFooter />
    </div>
  );
};

export default ForClinics;
